/**
 * Help Center - Left sidebar with categorized feature list, right panel with detailed help content.
 * Clean scrollbar-free design with linked references.
 */

import { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import {
  FEATURES, CATEGORIES, PLAN_NAMES, getFeaturesByCategory,
  type Feature, type FeatureCategory,
} from '@/config/features';
import {
  Search, ChevronRight, Lightbulb, BookOpen, Zap, CheckCircle,
  Sparkles, MessageSquare, Upload, Library, Users, BarChart3, Link2,
  Shield, ShieldAlert, Calculator, CalendarDays, FileText, Scale,
  GitCompare, AlertOctagon, CheckCheck, AlertTriangle, FileCheck,
  FileSearch, Building2, Mail, Rocket, HelpCircle, Lock,
  ArrowRight, ExternalLink,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/store/authStore';

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  MessageSquare, Upload, Library, Users, BarChart3, Link2, Shield,
  ShieldAlert, Calculator, Zap, CalendarDays, FileText, Scale,
  GitCompare, AlertOctagon, CheckCheck, AlertTriangle, FileCheck,
  FileSearch, BookOpen, Building2, Mail, Rocket, Sparkles, HelpCircle, Lock,
};

function getIcon(name: string) {
  return ICON_MAP[name] || Sparkles;
}

// ==================== Special Help Guides ====================

const PROMPTING_GUIDE: Feature = {
  slug: 'prompting-guide',
  name: 'Prompting Guide',
  description: 'Learn how to write effective prompts to get the best results from RoniDoc AI.',
  category: 'getting-started',
  icon: 'MessageSquare',
  minPlan: 0,
  highlight: 'The secret to getting great AI answers is asking great questions',
  whyItMatters: [
    'Well-crafted prompts get 10x better answers than vague questions',
    'Understanding how AI interprets your questions helps you get precise results',
    'Good prompts save time because you get the right answer on the first try',
    'Mastering prompting turns RoniDoc from a search tool into a strategic advisor',
  ],
  howToUse: [
    'Be specific: "What is the CGT discount for assets held over 12 months by an individual?" not "Tell me about CGT"',
    'Provide context: "For a small business with turnover under $10M, what instant asset write-off is available in FY2025?"',
    'Ask for comparisons: "Compare the tax treatment of a company vs trust for a business earning $500K"',
    'Request specific formats: "Give me a table comparing the BAS labels for GST-free vs input-taxed supplies"',
    'Use follow-ups: ask a broad question first, then drill down into specific areas',
    'Specify the date: "Show me the balance sheet as at 30 June 2025" so the AI fetches data for that exact date',
    'Mention benchmarks: include "benchmark" or "ATO" in your question to trigger industry comparison analysis',
    'Ask for action items: "What should I do before 30 June to minimise tax for this client?"',
  ],
  tips: [
    'Start with "For [client type], what..." to give the AI context about the entity type',
    'Ask "Why?" after getting an answer to get deeper analysis with reasoning',
    'Use the "Explain More" button for cited answers to get a comprehensive breakdown',
    'Try asking the same question with "Include Client Files" on vs off to see different perspectives',
    'For Xero data, specify exact dates, periods, or "current year vs last year"',
    'Combine data sources: "Based on the tax return and Xero data, is the depreciation schedule correct?"',
    'Ask for checklists: "Give me a year-end checklist for a company with turnover over $50M"',
    'Request risk analysis: "What are the top 3 audit risk areas for this client?"',
  ],
};

const GETTING_STARTED_GUIDE: Feature = {
  slug: 'getting-started-guide',
  name: 'Getting Started',
  description: 'Everything you need to know to set up and start using RoniDoc effectively.',
  category: 'getting-started',
  icon: 'Rocket',
  minPlan: 0,
  route: '/panel/features',
  highlight: 'From signup to your first AI-powered insight in under 5 minutes',
  whyItMatters: [
    'RoniDoc is designed to be productive from day one with no training required',
    'The onboarding wizard guides you through essential setup steps',
    'Connect Xero and upload documents to unlock the full power of AI analysis',
    'Every feature is accessible from the sidebar with contextual help',
  ],
  howToUse: [
    'Complete the onboarding wizard: firm details, invite team, connect Xero',
    'Add your first client and upload a few key documents (tax returns, financials)',
    'Open the client workspace and ask AI a question about the uploaded documents',
    'Connect Xero to pull live financial data such as Balance Sheet, P&L, and Trial Balance',
    'Try the [Compliance Checker](/panel/compliance/checker) to see how your client compares to ATO benchmarks',
    'Explore the [Feature Marketplace](/panel/features) to discover all available tools',
    'Set up the AI Business Assistant App for email, calendar, and task management on your mobile device',
  ],
  tips: [
    'Start with one client to learn the workflow, then scale to all your clients',
    'The AI gets smarter with more data so upload as many relevant documents as possible',
    'Use keyboard shortcuts: Enter to send messages, Shift+Enter for new lines',
    'Check the usage indicator in the top right to monitor your quota',
    'Invite your team early because collaboration makes the platform more valuable',
    'Set your client industries correctly as this enables ATO benchmark comparisons',
  ],
};

const QBO_CONNECTION_GUIDE: Feature = {
  slug: 'connect-quickbooks',
  name: 'Connecting a Client\'s QuickBooks',
  description: 'Step-by-step guide to authorise a client\'s QuickBooks Online so RoniDoc can pull their P&L, Balance Sheet, and Trial Balance into chat and document automation.',
  category: 'getting-started',
  icon: 'Link2',
  minPlan: 0,
  route: '/panel/files/clients',
  highlight: 'Two clicks per client once you have a QuickBooks login set up',
  whyItMatters: [
    'RoniDoc loads each client\'s financials read-only from QuickBooks on demand - no manual report uploads',
    'Once connected, the AI chat and document automation auto-fill values like revenue, net profit, ABN, addresses and bank details directly from QuickBooks',
    'Each QuickBooks book is a separate, revocable authorisation - clients always retain control',
    'Tokens are AES-256 encrypted and auto-refresh every hour; you never share a password with RoniDoc',
  ],
  howToUse: [
    '**Step 1 - Set up a QuickBooks login (one-time).** QuickBooks does not have a partner-wide token like Xero, so you need a way to log in to each client\'s book. The recommended path is **QuickBooks Online Accountant (QBOA)** - Intuit\'s free dashboard for accountants.',
    'To get QBOA: visit `quickbooks.intuit.com/online/accountants`, sign up free with your firm email, then ask each client to invite you as their accountant from inside their own QuickBooks (Settings → Manage users → Invite accountant). Their book then appears on your QBOA dashboard automatically.',
    'Alternative: if you have direct access to a client\'s QuickBooks login (some firms manage it on the client\'s behalf), you can use that login at the connection step instead.',
    '**Step 2 - Connect a client.** In RoniDoc go to [Clients](/panel/files/clients), click on the client\'s name to open the right-side panel, scroll to the **Accounting System** section, and click the green **QuickBooks** tile.',
    'You\'ll be redirected to **intuit.com**. Log in with your QBOA (or the client\'s) credentials. Pick the QuickBooks company that matches the RoniDoc client. Click **Connect**.',
    'Intuit returns you to RoniDoc and the Accounting System section now shows **QuickBooks ✓** with the company name and a small **Disconnect** button.',
    '**Step 3 - Verify it works.** Open the AI chat sidebar on the same client and ask "What was revenue last financial year?" - the answer should pull straight from QuickBooks with no manual upload needed.',
    '**Switching providers.** Each client uses one accounting system at a time. To swap from Xero to QuickBooks (or back), click **Change** in the Accounting System section and pick the new provider.',
  ],
  tips: [
    'QBOA is strongly recommended even though it\'s optional - it cuts authorisation from "log in with the client\'s password" to a single click per client',
    'You only need to set up QBOA once for your firm; per-client connection then takes about 30 seconds each',
    'Connections never expire as long as they\'re used at least every 100 days - the access token auto-refreshes hourly',
    'Disconnect at any time from RoniDoc or from inside QuickBooks (Apps page) - both sides immediately lose access',
    'RoniDoc only requests **read-only** scopes: it can read P&L, Balance Sheet, Trial Balance and CompanyInfo. It cannot post transactions, edit records, or change anything in your client\'s books.',
    'If a connection fails, the most common cause is a redirect URI mismatch - check the Help Center or contact support@data-corner.com.au',
    'Use the same Accounting System section for Xero (blue tile) - same UX, no learning curve when you have a mixed-software client base',
  ],
};

const ALL_HELP_TOPICS = [GETTING_STARTED_GUIDE, PROMPTING_GUIDE, QBO_CONNECTION_GUIDE, ...FEATURES];

// Parse a small subset of inline markdown into JSX:
//   [text](url)   → router link (internal) or anchor (external)
//   **text**      → <strong>
//   `text`        → <code>
//   *text*        → <em>   (single asterisk, applied last so it doesn't eat **)
function renderTextWithLinks(text: string): React.ReactNode {
  // Split on the union of every supported pattern. Capture-group keeps the
  // delimiters in the resulting array so we can identify each chunk.
  const SPLIT_RE = /(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|`[^`]+`|\*[^*\n]+\*)/g;
  const parts = text.split(SPLIT_RE);

  return parts.map((part, i) => {
    if (!part) return null;

    const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) {
      const isExternal = /^https?:\/\//.test(link[2]);
      if (isExternal) {
        return (
          <a key={i} href={link[2]} target="_blank" rel="noreferrer" className="text-[#5048e5] hover:underline font-medium">
            {link[1]}
          </a>
        );
      }
      return (
        <Link key={i} to={link[2]} className="text-[#5048e5] hover:underline font-medium">
          {link[1]}
        </Link>
      );
    }

    const bold = part.match(/^\*\*([^*]+)\*\*$/);
    if (bold) return <strong key={i} className="font-semibold text-foreground">{bold[1]}</strong>;

    const code = part.match(/^`([^`]+)`$/);
    if (code) return (
      <code key={i} className="px-1 py-0.5 rounded bg-muted text-[0.85em] font-mono">{code[1]}</code>
    );

    const italic = part.match(/^\*([^*\n]+)\*$/);
    if (italic) return <em key={i}>{italic[1]}</em>;

    return <span key={i}>{part}</span>;
  });
}

export default function HelpCenterPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [selectedSlug, setSelectedSlug] = useState(searchParams.get('topic') || 'getting-started-guide');
  const navigate = useNavigate();
  const { hasPlanTier } = useAuthStore();
  const grouped = getFeaturesByCategory();

  const selectedTopic = ALL_HELP_TOPICS.find(f => f.slug === selectedSlug) || GETTING_STARTED_GUIDE;

  useEffect(() => {
    setSearchParams({ topic: selectedSlug }, { replace: true });
  }, [selectedSlug]);

  const matchesSearch = (f: Feature) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return f.name.toLowerCase().includes(q) || f.description.toLowerCase().includes(q);
  };

  const Icon = getIcon(selectedTopic.icon);
  const plan = PLAN_NAMES[selectedTopic.minPlan];
  const hasAccess = hasPlanTier(selectedTopic.minPlan);

  return (
    <div className="flex h-[calc(100vh-3.5rem)] -m-4 md:-m-6">
      {/* Left Sidebar */}
      <div className="w-64 lg:w-72 border-r flex flex-col shrink-0 bg-muted/10 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        {/* Search */}
        <div className="p-3 border-b sticky top-0 bg-background/95 backdrop-blur-sm z-10">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search help topics..."
              className="pl-8 h-8 text-xs"
            />
          </div>
        </div>

        {/* Topic list */}
        <div className="p-2 space-y-4">
          {/* Special guides */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground px-2 mb-1">Guides</p>
            {[GETTING_STARTED_GUIDE, PROMPTING_GUIDE, QBO_CONNECTION_GUIDE].filter(matchesSearch).map(topic => (
              <SidebarItem
                key={topic.slug}
                topic={topic}
                isActive={selectedSlug === topic.slug}
                onClick={() => setSelectedSlug(topic.slug)}
              />
            ))}
          </div>

          {/* Features by category */}
          {Object.entries(CATEGORIES).map(([catKey, cat]) => {
            const catFeatures = (grouped[catKey as FeatureCategory] || []).filter(matchesSearch);
            if (catFeatures.length === 0) return null;
            return (
              <div key={catKey}>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground px-2 mb-1">
                  {cat.label}
                </p>
                {catFeatures.map(topic => (
                  <SidebarItem
                    key={topic.slug}
                    topic={topic}
                    isActive={selectedSlug === topic.slug}
                    onClick={() => setSelectedSlug(topic.slug)}
                  />
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {/* Right Content */}
      <div className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        <div className="max-w-3xl p-6 md:p-8 space-y-8">
          {/* Header */}
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className={cn(
                'h-10 w-10 rounded-xl flex items-center justify-center shrink-0',
                hasAccess ? 'bg-[#5048e5]/10 text-[#5048e5]' : 'bg-muted text-muted-foreground'
              )}>
                <Icon className="h-5 w-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-bold">{selectedTopic.name}</h1>
                  <Badge className={cn('text-[9px] px-1.5 py-0', plan.bg, plan.color, 'border-0')}>
                    {plan.name}
                  </Badge>
                </div>
              </div>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">{selectedTopic.description}</p>
            {selectedTopic.highlight && (
              <p className="text-[#5048e5] text-sm font-medium mt-2 italic">"{selectedTopic.highlight}"</p>
            )}
            {hasAccess && selectedTopic.route && (
              <Button
                onClick={() => navigate(selectedTopic.route!)}
                size="sm"
                className="bg-[#5048e5] hover:bg-[#443fcc] mt-3"
              >
                Open {selectedTopic.name} <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            )}
          </div>

          {/* Why It Matters */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Zap className="h-4 w-4 text-amber-500" />
              <h2 className="text-sm font-semibold uppercase tracking-wider">Why It Matters</h2>
            </div>
            <div className="space-y-3 pl-1">
              {selectedTopic.whyItMatters.map((point, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                  <p className="text-sm leading-relaxed">{renderTextWithLinks(point)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* How to Use */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="h-4 w-4 text-[#5048e5]" />
              <h2 className="text-sm font-semibold uppercase tracking-wider">How to Use</h2>
            </div>
            <ol className="space-y-3 pl-1">
              {selectedTopic.howToUse.map((step, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="h-5 w-5 rounded-full bg-[#5048e5]/10 text-[#5048e5] flex items-center justify-center shrink-0 text-[10px] font-bold mt-0.5">
                    {i + 1}
                  </span>
                  <p className="text-sm leading-relaxed">{renderTextWithLinks(step)}</p>
                </li>
              ))}
            </ol>
          </div>

          {/* Pro Tips */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Lightbulb className="h-4 w-4 text-green-500" />
              <h2 className="text-sm font-semibold uppercase tracking-wider">Pro Tips</h2>
            </div>
            <div className="space-y-2 pl-1">
              {selectedTopic.tips.map((tip, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-green-50/50 dark:bg-green-950/20">
                  <Lightbulb className="h-3.5 w-3.5 text-green-500 shrink-0 mt-0.5" />
                  <p className="text-sm leading-relaxed">{renderTextWithLinks(tip)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Upgrade CTA for locked features */}
          {!hasAccess && (
            <div className="rounded-xl border-2 border-dashed border-[#5048e5]/20 bg-[#5048e5]/5 p-6 text-center">
              <Lock className="h-8 w-8 text-[#5048e5]/30 mx-auto mb-2" />
              <h3 className="font-semibold mb-1">Unlock {selectedTopic.name}</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Available on the {plan.name} plan and above.
              </p>
              <Button onClick={() => navigate('/panel/upgrade')} size="sm" className="bg-[#5048e5] hover:bg-[#443fcc]">
                Upgrade Now <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </div>
          )}

          {/* Related topics */}
          {selectedTopic.relatedSlugs && selectedTopic.relatedSlugs.length > 0 && (
            <div>
              <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">Related Topics</h3>
              <div className="flex flex-wrap gap-2">
                {selectedTopic.relatedSlugs.map(rs => {
                  const related = ALL_HELP_TOPICS.find(f => f.slug === rs);
                  if (!related) return null;
                  const RIcon = getIcon(related.icon);
                  return (
                    <button
                      key={rs}
                      onClick={() => { setSelectedSlug(rs); window.scrollTo(0, 0); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs hover:bg-muted transition"
                    >
                      <RIcon className="h-3 w-3 text-muted-foreground" />
                      {related.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Bottom spacer */}
          <div className="h-8" />
        </div>
      </div>
    </div>
  );
}

// ==================== Sub Components ====================

function SidebarItem({ topic, isActive, onClick }: { topic: Feature; isActive: boolean; onClick: () => void }) {
  const Icon = getIcon(topic.icon);
  const plan = PLAN_NAMES[topic.minPlan];

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-xs transition-all',
        isActive
          ? 'bg-[#5048e5]/10 text-[#5048e5] font-medium'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
      )}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" />
      <span className="truncate flex-1">{topic.name}</span>
      {topic.minPlan > 0 && (
        <span className={cn('text-[8px] px-1 rounded', plan.bg, plan.color)}>{plan.name}</span>
      )}
    </button>
  );
}
